import React, { useState, useRef, useEffect } from "react"
import Popup from "reactjs-popup"
import { useDispatch, useSelector } from "react-redux"
import { useAuth0 } from "@auth0/auth0-react"
import ConfirmationDialog from "../DailyCalendarFiles/CustomCalendar/ConfirmationDialog"
import {
	setIsEditing,
	transferPatientsBetweenNursesAndChairs
} from "../../features/schedule/scheduleSlice"
import { transferPatientsBetweenNursesAndChairsTomorrow } from "../../features/schedule/tomorrowSlice"
import { transferPatientsBetweenNursesAndChairsSandbox } from "../../features/schedule/sandboxSlice"
import store from "../../store"
import { IoIosSwap } from "react-icons/io"
import { useSchedulePatch } from "../../hooks/useSchedulePatch"
import {
	endTimeAllCases,
	currentlyScheduledCompleted,
	currentlyRealTimeCompleted,
	confirmedCancellationNoShow
} from "../../utils/realTimeUtils"
import {
	getDateFormatted,
	getFirstOpenDateToday,
	getFirstOpenDateTomorrow
} from "../../utils/generalUtils"
import useLogging from "../../hooks/useLogging"
import useFetchSettings from "../../hooks/useFetchSettings"
import { getNursePatients } from "../../features/schedule/derivedAssignments"
const SwapAssignmentPopup = ({
	selectedNurse,
	setNotification,
	isOpen,
	onClose,
	patientSchedule,
	nurses
}) => {
	const dispatch = useDispatch()
	const { getAccessTokenSilently } = useAuth0()
	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [targetNurse, setTargetNurse] = useState(null)
	const scrollableRef = useRef(null)
	const [isSaving, setIsSaving] = useState(false)
	const [searchTerm, setSearchTerm] = useState("")

	const { settingsData } = useFetchSettings()
	const { user } = useAuth0()

	const logging = useLogging()

	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []
	const nextDate = getDateFormatted(
		getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
	)

	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)
	const schedulePatch = useSchedulePatch({ isTomorrowMode, sandbox })

	const date = isTomorrowMode
		? nextDate
		: getDateFormatted(getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates))

	const handleClose = () => {
		if (!isSaving) {
			dispatch(setIsEditing(false))
		}
		setSearchTerm("")
		logging.info("SwapAssignmentPopup - Popup closed", {
			component: "SwapAssignmentPopup",
			function: "handleClose",
			nurseSwapping: selectedNurse
		})
		onClose()
	}

	const handleOpen = () => {
		// When the popup opens, scroll to top
		if (scrollableRef.current) {
			scrollableRef.current.scrollTop = 0
		}
		logging.info("SwapAssignmentPopup - Popup opened", {
			component: "SwapAssignmentPopup",
			function: "handleOpen",
			nurseSwapping: selectedNurse
		})
	}

	const getPatientsToSwap = (nurse, otherNurse, isTomorrowMode) => {
		const isPatientEligible = patient => {
			const isScheduled =
				sandbox || isTomorrowMode
					? !currentlyScheduledCompleted(patient, sandbox, isTomorrowMode) &&
						!confirmedCancellationNoShow(patient)
					: !currentlyRealTimeCompleted(patient) && !confirmedCancellationNoShow(patient)

			const endTime = endTimeAllCases(patient, sandbox, isTomorrowMode)
			return (
				isScheduled &&
				otherNurse.scheduledStartTime < endTime &&
				endTime <= otherNurse.scheduledEndTime
			)
		}

		return getNursePatients(patientSchedule, nurse.nurseId).filter(isPatientEligible)
	}

	const handleSwapNurse = nurse => {
		logging.info("SwapAssignmentPopup - Nurse selected", {
			component: "SwapAssignmentPopup",
			function: "handleSwapNurse",
			nurseSwapping: selectedNurse,
			nurseToSwapWith: nurse
		})
		// Check unstarted patients for both nurses
		const selectedNurseSwapPatients = getPatientsToSwap(selectedNurse, nurse, isTomorrowMode)

		const targetNurseSwapPatients = getPatientsToSwap(nurse, selectedNurse, isTomorrowMode)

		// If either nurse has no patients to swap, show notification instead of dialog
		if (selectedNurseSwapPatients.length === 0 && targetNurseSwapPatients.length === 0) {
			setNotification({
				visible: true,
				message: "Not possible to swap assignments: no patients found to swap",
				success: false
			})
			setTimeout(() => setNotification({ visible: false, message: "" }), 3000)
			return
		}

		// Otherwise proceed with normal swap dialog
		setTargetNurse(nurse)
		setIsDialogOpen(true)
	}

	const swapNurseAssignments = (selectedNurse, targetNurse) => {
		const currentState =
			store.getState()[isTomorrowMode ? "tomorrow" : sandbox ? "sandbox" : "schedule"]

		// Find the nurses in the updated schedule
		const nurse1 = currentState.nurseSchedule.find(n => n.nurseId === selectedNurse.nurseId)
		const nurse2 = currentState.nurseSchedule.find(n => n.nurseId === targetNurse.nurseId)

		const nurse1Patients = getPatientsToSwap(nurse1, nurse2, isTomorrowMode).map(
			patient => patient.patientId
		)

		const nurse2Patients = getPatientsToSwap(nurse2, nurse1, isTomorrowMode).map(
			patient => patient.patientId
		)

		// Create reassignments array
		const reassignments = [
			// Move nurse1's patients to nurse2
			...nurse1Patients.map(patientId => ({
				patientId,
				newNurseId: nurse2.nurseId
			})),
			// Move nurse2's patients to nurse1
			...nurse2Patients.map(patientId => ({
				patientId,
				newNurseId: nurse1.nurseId
			}))
		]

		// Dispatch appropriate action based on mode
		if (sandbox) {
			dispatch(transferPatientsBetweenNursesAndChairsSandbox(reassignments))
		} else if (isTomorrowMode) {
			dispatch(transferPatientsBetweenNursesAndChairsTomorrow(reassignments))
		} else {
			dispatch(transferPatientsBetweenNursesAndChairs(reassignments))
		}
	}

	const confirmSwap = async () => {
		logging.info("SwapAssignmentPopup - Confirming swap", {
			component: "SwapAssignmentPopup",
			function: "confirmSwap",
			nurseSwapping: selectedNurse,
			nurseToSwapWith: targetNurse
		})
		setIsDialogOpen(false)
		setIsSaving(true)
		dispatch(setIsEditing(true))

		try {
			// Find the nurses in the updated schedule
			swapNurseAssignments(selectedNurse, targetNurse)

			if (!sandbox) {
				try {
					await getAccessTokenSilently({
						audience: process.env.REACT_APP_API_IDENTIFIER
					})

					await schedulePatch.mutateAsync({ date, setNotification })
				} catch (error) {
					logging.error("Error saving schedule:", {
						error: error.message,
						stack: error.stack
					})
				}
			}

			dispatch(setIsEditing(false))

			setNotification({
				visible: true,
				message: "Successfully swapped nurse assignments!",
				success: true
			})
			setTimeout(() => setNotification({ visible: false, message: "" }), 3000)
		} catch (error) {
			logging.error("Error swapping nurse assignments:", {
				error: error.message,
				stack: error.stack
			})
			setNotification({
				visible: true,
				message: "Error swapping nurse assignments",
				success: false
			})
		} finally {
			logging.info("SwapAssignmentPopup - Swap completed", {
				component: "SwapAssignmentPopup",
				function: "confirmSwap",
				nurseSwapping: selectedNurse,
				nurseToSwapWith: targetNurse
			})
			setIsSaving(false) // Reset saving state
			setTimeout(() => setNotification({ visible: false, message: "" }), 3000)
		}
	}

	const cancelSwap = () => {
		logging.info("SwapAssignmentPopup - Swap cancelled", {
			component: "SwapAssignmentPopup",
			function: "cancelSwap",
			nurseSwapping: selectedNurse,
			nurseToSwapWith: targetNurse
		})
		setIsDialogOpen(false)
	}

	useEffect(() => {
		// Set scroll to top whenever the component is mounted
		if (scrollableRef.current) {
			scrollableRef.current.scrollTop = 0
		}
	}, []) // Empty dependency ensures it runs only on mount

	return (
		<>
			<Popup
				open={isOpen}
				onOpen={handleOpen}
				onClose={handleClose}
				modal
				nested
				contentStyle={{ width: "auto", border: "none", borderRadius: "10px", padding: "0" }}
				overlayStyle={{ background: "rgba(0, 0, 0, 0.5)" }}
			>
				{close => (
					<div
						className="bg-white p-6 rounded-2xl shadow-lg w-96 mx-auto max-h-96 overflow-y-auto"
						ref={scrollableRef}
					>
						<div className="flex items-center text-xl font-semibold mb-6 text-[#091556]">
							<IoIosSwap className="w-5 h-5 mr-2" />
							Select Nurse to Swap With
						</div>
						<div className="mb-4">
							{/* Search box for filtering nurses */}
							<input
								type="text"
								placeholder="Search for a nurse..."
								className="border border-gray-300 rounded-md p-2 mb-4 w-full"
								onChange={e => {
									setSearchTerm(e.target.value)
								}}
							/>

							{nurses &&
								nurses
									.filter(
										nurse =>
											nurse.nurseId !== selectedNurse?.nurseId &&
											nurse.nurseName
												.toLowerCase()
												.includes(searchTerm.toLowerCase()) // Filter based on search term
									)
									.map(nurse => (
										<div
											key={nurse.nurseId}
											className="cursor-pointer p-2 flex items-center hover:bg-[#F3F4F6] rounded-md"
											onClick={() => {
												handleSwapNurse(nurse)
												close()
											}}
										>
											<div className="flex items-center justify-center w-7 h-7 bg-[#959DC5] text-white font-semibold rounded-full text-xs mr-3">
												{nurse.nurseName
													.split(" ")
													.map(n => n[0])
													.join("")}
											</div>
											<span className="text-[#091556] font-mum">
												{nurse.nurseName}
											</span>
										</div>
									))}
						</div>
						<div className="flex justify-end">
							<button
								onClick={onClose}
								className="bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-gray-300 transition"
							>
								Cancel
							</button>
						</div>
					</div>
				)}
			</Popup>

			<ConfirmationDialog
				title="Confirm Swap"
				message={`Are you sure you want to swap all ongoing and future patient assignments between ${selectedNurse?.nurseName} and ${targetNurse?.nurseName}?`}
				onConfirm={confirmSwap}
				onCancel={cancelSwap}
				isOpen={isDialogOpen}
				onClose={() => setIsDialogOpen(false)}
			/>
		</>
	)
}

export default SwapAssignmentPopup
